import { tr } from '@/lib/i18n-core';
import { useMemo } from 'react';
import { useInfiniteQuery, type QueryKey } from '@tanstack/react-query';
import { get, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { Alert, Button } from '@/components/ui/primitives';
import { nextCursorOf, type QueryParams } from './paging-export';

export function useCursorList<T extends { id: string }>({
  queryKey,
  path,
  params,
  pageSize,
  enabled,
}: {
  queryKey: QueryKey;
  path: string;
  params: QueryParams;
  pageSize: number;
  enabled: boolean;
}) {
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => get<T[]>(`${path}${qs({ ...params, limit: pageSize, cursor: pageParam })}`),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => nextCursorOf(lastPage, pageSize),
    enabled,
  });

  const rows = useMemo(() => query.data?.pages.flat(), [query.data]);

  return {
    rows,
    isLoading: query.isLoading,
    firstPageError: rows === undefined ? query.error : null,
    nextPageError: rows !== undefined && query.isFetchNextPageError ? query.error : null,
    hasMore: query.hasNextPage,
    loadingMore: query.isFetchingNextPage,
    loadMore: () => void query.fetchNextPage(),
  };
}

export function LoadMore({
  hasMore,
  loading,
  error,
  onLoad,
}: {
  hasMore: boolean;
  loading: boolean;
  error: unknown;
  onLoad: () => void;
}) {
  if (!hasMore && !error) return null;
  return (
    <div className="mt-3 space-y-2">
      {error !== null && error !== undefined && <Alert variant="destructive">{describeError(error)}</Alert>}
      {hasMore && (
        <div className="flex justify-center">
          <Button type="button" variant="outline" size="sm" loading={loading} onClick={onLoad}>
            {tr('pharmacy', 'load_more')}
          </Button>
        </div>
      )}
    </div>
  );
}
